import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getPolizas, updatePoliza, deletePoliza } from "../../../services/divinaPolizasService";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription, CardAction } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Switch, RadioPills } from "../../../components/ui/switch";
import { Badge } from "../../../components/ui/badge";
import { PlusCircle, Pencil, FileText, Trash2 } from "lucide-react";
import { EmptyState } from "../../../components/ui/empty-state";

export default function ColectivePolizasList() {
  const [polizas, setPolizas] = useState([]);
  const [filteredPolizas, setFilteredPolizas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todos");

  useEffect(() => {
    loadPolizas();
  }, []);

  useEffect(() => {
    filterPolizas();
  }, [polizas, filter]);

  const loadPolizas = async () => {
    try {
      setLoading(true);
      const data = await getPolizas();
      setPolizas(data);
    } catch (error) {
      console.error("Error loading polizas:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterPolizas = () => {
    if (filter === "todos") {
      setFilteredPolizas(polizas);
    } else {
      setFilteredPolizas(polizas.filter(p => p.estado === filter));
    }
  };

  const toggleEstado = async (poliza) => {
    const nuevo = poliza.estado === "vigente" ? "suspendida" : "vigente";
    try {
      await updatePoliza(poliza.id, { ...poliza, estado: nuevo });
      setPolizas(polizas.map(p => p.id === poliza.id ? { ...p, estado: nuevo } : p));
    } catch (error) {
      console.error("Error updating poliza estado:", error);
    }
  };

  const handleDelete = async (poliza) => {
    try {
      await deletePoliza(poliza.id);
      setPolizas(polizas.filter(p => p.id !== poliza.id));
    } catch (error) {
      console.error("Error deleting poliza:", error);
    }
  };

  return (
    <div className="@container/main p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Divina Polizas Colectivas</h1>
          <p className="text-gray-600">Gestión de polizas colectivas anticipadas</p>
        </div>
        <Link to="/product_config/polizas/new">
          <Button className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white">
            <PlusCircle className="h-5 w-5" />
            Nueva Poliza
          </Button>
        </Link>
      </div>

      {/* Radio pill del Yool DS: un estado entre varios, todos a la vista.
          Ver components/ui/switch.jsx. */}
      <RadioPills
        aria-label="Filtrar pólizas por estado"
        value={filter}
        onChange={setFilter}
        options={[
          { value: "todos", label: "Todos" },
          ...["vigente", "suspendida", "terminada", "anulada"].map((e) => ({
            value: e,
            label: e.charAt(0).toUpperCase() + e.slice(1),
          })),
        ]}
      />

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 px-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
          {filteredPolizas.length === 0 ? (
            <div className="col-span-full">
              <EmptyState
                icon={<FileText />}
                title="Sin pólizas"
                description="No hay pólizas disponibles con el filtro seleccionado."
              />
            </div>
          ) : (
            filteredPolizas.map(poliza => (
              <Card key={poliza.id} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm hover:bg-white hover:scale-105">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 group-hover:from-blue-600 group-hover:to-blue-700 transition-all duration-300">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200">
                      {poliza.estado}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <CardDescription className="text-slate-600 font-medium">{poliza.plan?.nombre || 'Sin plan'}</CardDescription>
                    <CardTitle className="text-3xl font-bold text-slate-800 flex items-center gap-2">
                      {poliza.numero_poliza || poliza.numero_contrato || 'Sin número'}
                    </CardTitle>
                  </div>
                  <div className="pt-2">
                    <div className="flex items-center gap-2">
                      <Switch checked={poliza.estado === "vigente"} onCheckedChange={() => toggleEstado(poliza)} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {poliza.url_poliza && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{poliza.url_poliza}</p>
                  )}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {poliza.compania?.nombre_corto && (
                      <div className="space-y-1">
                        <span className="font-medium text-muted-foreground">Compañía</span>
                        <div className="font-mono">{poliza.compania?.nombre_corto}</div>
                      </div>
                    )}
                    {poliza.ramo?.nombre && (
                      <div className="space-y-1">
                        <span className="font-medium text-muted-foreground">Ramo</span>
                        <div className="font-mono">{poliza.ramo?.nombre}</div>
                      </div>
                    )}
                    {poliza.contratante?.nombre_razon_social && (
                      <div className="space-y-1">
                        <span className="font-medium text-muted-foreground">Contratante</span>
                        <div className="font-mono">{poliza.contratante?.nombre_razon_social}</div>
                      </div>
                    )}
                    {poliza.fecha_inicio && (
                      <div className="space-y-1">
                        <span className="font-medium text-muted-foreground">Fecha Inicio</span>
                        <div className="font-mono">{new Date(poliza.fecha_inicio).toLocaleDateString()}</div>
                      </div>
                    )}
                    {poliza.moneda && (
                      <div className="space-y-1">
                        <span className="font-medium text-muted-foreground">Moneda</span>
                        <div className="font-mono">{poliza.moneda}</div>
                      </div>
                    )}
                    {poliza.prima_total && (
                      <div className="space-y-1">
                        <span className="font-medium text-muted-foreground">Prima Total</span>
                        <div className="font-semibold">{poliza.prima_total}</div>
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="pt-0">
                  <div className="flex w-full justify-end gap-2">
                    <Link to={`/product_config/polizas/edit/${poliza.id}`}>
                      <Button variant="outline" size="sm">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-[var(--yool-error)] hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(poliza)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}